/**
 * Summarise a host view's already-loaded edges into `RelationshipSummary[]`
 * for the selected node. 1-hop only (only edges touching `nodeId`).
 *
 * See design D7b. The Relationships tab groups by `type` downstream.
 */

import type { GraphEdge, GraphNode } from '@/components/neo4j/cytoscape/types'
import type { RelationshipSummary } from './types'

function pickName(node: GraphNode | undefined, fallbackId: string): string {
  if (!node) return fallbackId
  return node.name || node.typeLabel || fallbackId
}

function pickLabels(node: GraphNode | undefined): string[] {
  if (!node) return []
  return node.labels ?? []
}

export function summarizeEdges(
  nodeId: string,
  edges: GraphEdge[],
  nodes: GraphNode[]
): RelationshipSummary[] {
  const nodeMap = new Map<string, GraphNode>()
  for (const n of nodes) nodeMap.set(n.id, n)

  const summaries: RelationshipSummary[] = []
  for (const e of edges) {
    const touchesSource = e.source === nodeId
    const touchesTarget = e.target === nodeId
    if (!touchesSource && !touchesTarget) continue

    const direction: RelationshipSummary['direction'] = touchesSource ? 'outgoing' : 'incoming'
    const sourceNode = nodeMap.get(e.source)
    const targetNode = nodeMap.get(e.target)

    summaries.push({
      id: e.id,
      type: e.type,
      sourceId: e.source,
      sourceName: pickName(sourceNode, e.source),
      sourceLabels: pickLabels(sourceNode),
      targetId: e.target,
      targetName: pickName(targetNode, e.target),
      targetLabels: pickLabels(targetNode),
      direction,
      properties: e.properties,
    })
  }

  // Stable order by type → by source/target name, so the UI doesn't jitter
  // when React re-renders from a reshuffled edge array.
  summaries.sort((a, b) => {
    if (a.type !== b.type) return a.type.localeCompare(b.type)
    const aPeer = a.direction === 'outgoing' ? a.targetName : a.sourceName
    const bPeer = b.direction === 'outgoing' ? b.targetName : b.sourceName
    return aPeer.localeCompare(bPeer)
  })

  return summaries
}
